<template>
  <section class="work-rules">
    <section-divider title="Work rules">
      <template slot="actions">
        <schedule-action label="Schedule" />
        <a-button
          v-if="associate?.id && isFacilityCompany"
          :loading="loadingSetAvailabilityAlert"
          icon="notification"
          data-testid="profile-view-job-send-regular-schedule-gaps-alert-menu-item"
          @click="openSetAvailabilityAlertConfirm"
        >
          Send gaps alert
        </a-button>
      </template>
    </section-divider>

    <div class="work-rules__grid">
      <div class="work-rules__field">
        <span>Permanent</span>
        <a-switch
          :checked="associate.is_permanent"
          @change="value => handleAssociateFieldChange('is_permanent', value)"
        />
      </div>

      <template v-if="isVisibleWorkingHoursLimits">
        <div class="work-rules__field">
          <span>Max hours per day</span>
          <a-input-number
            class="work-rules__number"
            :min="0"
            :value="associate.max_working_hours_per_day"
            @change="value => handleAssociateFieldChange('max_working_hours_per_day', value || 0)"
          />
        </div>
        <div class="work-rules__field">
          <span>Max hours per week</span>
          <a-input-number
            class="work-rules__number"
            :min="0"
            :value="associate.max_working_hours_per_week"
            @change="value => handleAssociateFieldChange('max_working_hours_per_week', value || 0)"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { Button, InputNumber, Modal, notification, Switch } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import { deepCopy } from '@/utils/deep-copy'
import { isWorkingHoursPaymentType } from '@/modules/profile/utils/working-hours-limits'
import ProfileScheduleAction from '../schedule/profile-schedule-action.vue'
import SectionDivider from '@/components/ui/section-divider.vue'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')

export default {
  components: {
    'a-button': Button,
    'a-input-number': InputNumber,
    'a-switch': Switch,
    'schedule-action': ProfileScheduleAction,
    'section-divider': SectionDivider
  },
  data() {
    return {
      loadingSetAvailabilityAlert: false
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    isVisibleWorkingHoursLimits() {
      return isWorkingHoursPaymentType(this.associate.payment_info?.type)
    },

    isFacilityCompany() {
      return !this.$store.state.applicationState.currentProject.staffing
    }
  },
  methods: {
    ...profileActions(['sendSetAvailabilityAlert', 'updateAssociate', 'setAssociate']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async handleAssociateFieldChange(field, value) {
      const previousAssociate = deepCopy(this.associate)
      const nextAssociate = {
        ...this.associate,
        [field]: value
      }

      this.setAssociate(nextAssociate)

      try {
        await this.updateAssociate({
          associate: nextAssociate
        })
      } catch (error) {
        this.setAssociate(previousAssociate)
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    openSetAvailabilityAlertConfirm() {
      Modal.confirm({
        title: 'Send regular schedule gaps alert?',
        content:
          'The associate will receive an alert and can choose additional regular schedule gaps if any are available.',
        okText: 'Send alert',
        cancelText: 'Cancel',
        onOk: () => this.sendSetAvailabilityAlertConfirmed(),
        onCancel() {}
      })
    },

    async sendSetAvailabilityAlertConfirmed() {
      this.loadingSetAvailabilityAlert = true

      try {
        await this.sendSetAvailabilityAlert({
          associate_id: this.associate.id
        })

        this.showNotification('success', 'Success', 'Regular schedule gaps alert sent.')
      } catch (error) {
        this.showNotification(
          'error',
          'Error',
          'Alert could not be sent. Try again or contact support if the issue continues.'
        )
      } finally {
        this.loadingSetAvailabilityAlert = false
      }
    }
  }
}
</script>

<style scoped>
.work-rules {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.work-rules__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
}

.work-rules__field {
  min-width: 0;
}

.work-rules__field > span {
  display: block;
  margin-bottom: 4px;
  color: var(--text-color-secondary);
  font-size: 12px;
}

.work-rules__number {
  width: 120px;
}
</style>
